import { Injectable, inject } from '@angular/core';
import { FirebaseService } from './firebase.service.js';

export interface TwitchVideoData {
    id: string;
    stream_id: string;
    user_id: string;
    user_login: string;
    user_name: string;
    title: string;
    description: string;
    created_at: string;
    published_at: string;
    url: string;
    thumbnail_url: string;
    view_count: number;
    language: string;
    type: string;
    duration: string;
}

export interface ChannelVideosResponse {
    channel: {
        id: string;
        name: string;
        login: string;
        profileImageUrl: string;
    };
    videos: TwitchVideoData[];
    pagination?: { cursor?: string };
}

export interface HelixClip {
    id: string;
    url: string;
    embed_url: string;
    broadcaster_id: string;
    broadcaster_name: string;
    creator_id: string;
    creator_name: string;
    video_id: string;
    game_id: string;
    language: string;
    title: string;
    view_count: number;
    created_at: string;
    thumbnail_url: string;
    duration: number;
    vod_offset: number;
    is_featured: boolean;
}

export interface VodClipsResponse {
    clips: HelixClip[];
    cached: boolean;
    totalFetched: number;
    onVodCount: number;
    skippedNoOffset: number;
}

export type ClipsPeriod = 'all' | '24h' | '7d' | '30d';

export interface ChannelClipsResponse {
    channel: {
        id: string;
        name: string;
        login: string;
        profileImageUrl: string;
    };
    clips: HelixClip[];
    period?: ClipsPeriod;
    cached: boolean;
}

export interface ChannelSearchResult {
    id: string;
    login: string;
    displayName: string;
    profileImageUrl: string;
}

@Injectable({
    providedIn: 'root'
})
export class TwitchApiService {
    private readonly firebaseService = inject(FirebaseService);

    async getChannelVideos(channelName: string, cursor?: string, forceRefresh?: boolean): Promise<ChannelVideosResponse> {
        const cloudRunUrl = this.firebaseService.cloudRunApiUrl;

        if (!cloudRunUrl) {
            throw new Error('URL сервиса Cloud Run не настроен.');
        }

        try {
            const bodyPayload: any = { channelName };
            if (cursor) {
                bodyPayload.cursor = cursor;
            }
            if (forceRefresh) {
                bodyPayload.forceRefresh = true;
            }

            const headers = await this.firebaseService.buildCloudRunHeaders();
            if (!headers) {
                throw new Error('Войдите в аккаунт, чтобы получить список видео канала.');
            }

            const response = await fetch(`${cloudRunUrl}/get-channel-videos`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyPayload)
            });

            if (!response.ok) {
                const errorBody = await response.json().catch(() => ({}));
                throw new Error(errorBody.error || `HTTP Error ${response.status}`);
            }

            const data = await response.json();
            return data as ChannelVideosResponse;
        } catch (error: any) {
            console.error('Ошибка получения списка видео:', error);
            throw new Error(error.message || 'Не удалось получить список видео канала.');
        }
    }

    async getVodClips(params: {
        broadcasterId: string;
        vodId: string;
        startedAt: string;
        endedAt: string;
        isLive?: boolean;
        forceRefresh?: boolean;
    }): Promise<VodClipsResponse> {
        const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
        if (!cloudRunUrl) {
            throw new Error('URL сервиса Cloud Run не настроен.');
        }

        const headers = await this.firebaseService.buildCloudRunHeaders();
        if (!headers) {
            throw new Error('Войдите в аккаунт, чтобы загрузить клипы.');
        }

        const response = await fetch(`${cloudRunUrl}/get-vod-clips`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(errorBody.error || `HTTP Error ${response.status}`);
        }

        return await response.json() as VodClipsResponse;
    }

    async getChannelClips(channelName: string, period?: ClipsPeriod, forceRefresh?: boolean): Promise<ChannelClipsResponse> {
        const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
        if (!cloudRunUrl) throw new Error('URL сервиса Cloud Run не настроен.');

        const headers = await this.firebaseService.buildCloudRunHeaders();
        if (!headers) throw new Error('Войдите в аккаунт, чтобы получить топ-клипы канала.');

        const bodyPayload: any = { channelName };
        if (period && period !== 'all') bodyPayload.period = period;
        if (forceRefresh) bodyPayload.forceRefresh = true;

        const response = await fetch(`${cloudRunUrl}/get-channel-clips`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify(bodyPayload),
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(errorBody.error || `HTTP Error ${response.status}`);
        }

        return await response.json() as ChannelClipsResponse;
    }

    // Channel-name autocomplete. Helix /search/channels — prefix-match по broadcaster_login,
    // не fuzzy. Возвращает до 10 каналов; пустой результат = `[]`, не ошибка.
    // Errors swallowed at component level (silent dropdown), не дёргаем channelError.
    async searchChannels(query: string): Promise<ChannelSearchResult[]> {
        const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
        if (!cloudRunUrl) throw new Error('URL сервиса Cloud Run не настроен.');

        const headers = await this.firebaseService.buildCloudRunHeaders();
        if (!headers) throw new Error('Войдите в аккаунт, чтобы искать каналы.');

        const response = await fetch(`${cloudRunUrl}/search-channels`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ query }),
        });

        if (!response.ok) {
            throw new Error(`search-channels HTTP ${response.status}`);
        }

        const data = await response.json();
        return (data?.channels || []) as ChannelSearchResult[];
    }

    async getChannelHighlights(channelName: string, cursor?: string, forceRefresh?: boolean): Promise<ChannelVideosResponse> {
        const cloudRunUrl = this.firebaseService.cloudRunApiUrl;
        if (!cloudRunUrl) throw new Error('URL сервиса Cloud Run не настроен.');

        const headers = await this.firebaseService.buildCloudRunHeaders();
        if (!headers) throw new Error('Войдите в аккаунт, чтобы получить хайлайты канала.');

        const bodyPayload: any = { channelName };
        if (cursor) bodyPayload.cursor = cursor;
        if (forceRefresh) bodyPayload.forceRefresh = true;

        const response = await fetch(`${cloudRunUrl}/get-channel-highlights`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify(bodyPayload),
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(errorBody.error || `HTTP Error ${response.status}`);
        }

        return await response.json() as ChannelVideosResponse;
    }
}
